import { createNode, createOptional } from '@glueapp/graph-types';
import type { DataType, DataValue, FunctionDefinition, NodeInstanceDataPort } from '@glueapp/graph-types';

interface EndpointDefinition {
    id: string;
    method: string; // GET, POST, PUT, PATCH, DELETE, HEAD, ANY
    path: string; // e.g. '/users/:id'
    streaming: boolean;
    timeout: number;
    pathParams: { name: string; type: DataType }[];
    queryParams: { name: string; type: DataType; optional: boolean }[];
    requestBodyShape?: DataType;
    responseShape?: DataType;
    expectedHeaders?: string[];
}

interface ApiRequestPayload {
    requestId: string;
    endpointId: string;
    method: string;
    path: string;
    pathParams: Record<string, string>;
    queryParams: Record<string, string>;
    headers: Record<string, string>;
    body: unknown;
}

function parseParamValue(raw: string | undefined, type: DataType): DataValue {
    if (raw === undefined) return null;
    if (type.kind === 'number') {
        const parsed = Number(raw);
        return isNaN(parsed) ? null : parsed;
    }
    if (type.kind === 'boolean') return raw === 'true' || raw === '1';
    return raw;
}

const METHODS_WITH_BODY = ['POST', 'PUT', 'PATCH', 'DELETE', 'ANY'];

const headersListType: DataType = {
    kind: 'array',
    subType: {
        kind: 'object',
        properties: { name: { kind: 'string' }, value: { kind: 'string' } },
        restriction: 'exact',
    },
};

function mergeExpectedHeaders(apiHeaders: string[], endpointHeaders: string[]): string[] {
    const merged = new Map<string, string>();
    for (const name of apiHeaders) merged.set(name.toLowerCase(), name);
    for (const name of endpointHeaders) merged.set(name.toLowerCase(), name);
    return Array.from(merged.values());
}

// Auth function type signature — the inPort type that triggers executeFunctionNode injection
const authFunctionType: DataType = {
    kind: 'function',
    inputTypes: {
        'Authorization Header': { kind: 'string' },
        Headers: headersListType,
    },
    outputTypes: {
        Authenticated: { kind: 'boolean' },
    },
};

export const ApiServer = createNode({
    id: 'ApiServer',
    name: 'API Server',
    description: 'Define HTTP endpoints and trigger graph execution when requests are received',
    category: 'API Server',
    color: '#808080',
    hasExecutionDecisions: true,
    isAsync: true,
    inPorts: {},
    outPorts: {},
    interfaceComponentId: 'api-server-editor',
    transientStateKeys: ['apiId', 'authFunctionId'],
    hideInherentInPort: true,
    computeShape: input => {
        const authEnabled = input.state?.authEnabled as boolean | undefined;
        const authFunctionId = input.state?.authFunctionId as string | undefined;
        const endpoints = (input.state?.endpoints as unknown as EndpointDefinition[]) || [];
        const apiExpectedHeaders = (input.state?.apiExpectedHeaders as string[]) || [];

        const inPorts: NodeInstanceDataPort[] = [...input.nodeInPorts];
        const outPorts: NodeInstanceDataPort[] = [];

        // Auth function inPort + auth output ports
        if (authEnabled && authFunctionId) {
            inPorts.push({ name: 'Auth Function', type: authFunctionType });

            const authFunction = (input.allFunctions || []).find((func: FunctionDefinition) => func.id === authFunctionId);
            if (authFunction) {
                for (const portDef of authFunction.outputPortDefinitions) {
                    if (portDef.name !== 'Authenticated') {
                        outPorts.push({ name: `Auth: ${portDef.name}`, type: portDef.type });
                    }
                }
            }
        }

        // Endpoint output ports
        for (const endpoint of endpoints) {
            const prefix = `${endpoint.method} ${endpoint.path}`;

            // Trigger port — always first, null-typed (control-flow only)
            outPorts.push({
                name: prefix,
                type: { kind: 'null' },
            });

            // Path param ports
            for (const param of endpoint.pathParams) {
                outPorts.push({ name: `${prefix}: Path: ${param.name}`, type: param.type });
            }

            // Query param ports
            for (const param of endpoint.queryParams) {
                const portType = param.optional ? createOptional(param.type) : param.type;
                outPorts.push({ name: `${prefix}: Query: ${param.name}`, type: portType });
            }

            // Typed Headers port — object with expected header properties
            const endpointExpectedHeaders = endpoint.expectedHeaders || [];
            const combinedHeaders = mergeExpectedHeaders(apiExpectedHeaders, endpointExpectedHeaders);
            const headerProperties: Record<string, DataType> = {};
            for (const headerName of combinedHeaders) {
                headerProperties[headerName] = { kind: 'string' };
            }
            outPorts.push({
                name: `${prefix}: Headers`,
                type: { kind: 'object', properties: headerProperties, restriction: 'exact' },
            });

            // Headers List port — full array of all request headers
            outPorts.push({
                name: `${prefix}: Headers List`,
                type: headersListType,
            });

            // Body port — only for methods that have a body
            if (METHODS_WITH_BODY.includes(endpoint.method)) {
                outPorts.push({
                    name: `${prefix}: Body`,
                    type: endpoint.requestBodyShape || { kind: 'object', properties: {}, restriction: 'open' },
                });
            }

            // Method port — only for ANY
            if (endpoint.method === 'ANY') {
                outPorts.push({ name: `${prefix}: Method`, type: { kind: 'string' } });
            }
        }

        return { inPorts, outPorts };
    },
    execute: async input => {
        // @ts-expect-error - payload not typed through createNode
        const payload = input.payload as ApiRequestPayload;
        const endpoints = (input.state?.endpoints as unknown as EndpointDefinition[]) || [];
        const apiExpectedHeaders = (input.state?.apiExpectedHeaders as string[]) || [];

        const matchedEndpoint = endpoints.find(endpoint => endpoint.id === payload.endpointId);
        if (!matchedEndpoint) {
            return { output: {}, executionDecisions: {} };
        }

        const prefix = `${matchedEndpoint.method} ${matchedEndpoint.path}`;
        const output: Record<string, DataValue> = {};
        const executionDecisions: Record<string, boolean> = {};

        // --- Authentication ---
        const authFunctionRef = (input.inPorts as { 'Auth Function': DataValue })['Auth Function'] as
            | { isFunctionReferenceValue: true; functionId: string }
            | undefined;

        if (authFunctionRef && authFunctionRef.isFunctionReferenceValue) {
            const executeFunctionNode = input.executeFunctionNode as
                | ((functionId: string, inputs: Record<string, DataValue>) => Promise<{ output: Record<string, DataValue> }>)
                | undefined;

            if (executeFunctionNode) {
                const headersList = Object.entries(payload.headers).map(([name, value]) => ({ name, value }));
                const authHeaderName = (input.state?.authHeaderName as string) || 'X-API-Key';
                const authInputs: Record<string, DataValue> = {
                    'Authorization Header': payload.headers['authorization'] || payload.headers['Authorization'] || '',
                    Headers: headersList,
                    'Header Name': authHeaderName,
                    Body: (payload.body as DataValue) ?? null,
                    Method: payload.method,
                    Path: payload.path,
                    'Query Params': (payload.queryParams as unknown as DataValue) ?? null,
                };

                const authResult = await executeFunctionNode(authFunctionRef.functionId, authInputs);
                const authenticated = authResult.output.Authenticated as boolean;

                if (!authenticated) {
                    // Auth failed — disable all ports and return 401
                    for (const endpoint of endpoints) {
                        const endpointPrefix = `${endpoint.method} ${endpoint.path}`;
                        executionDecisions[endpointPrefix] = false;
                        for (const param of endpoint.pathParams) {
                            executionDecisions[`${endpointPrefix}: Path: ${param.name}`] = false;
                        }
                        for (const param of endpoint.queryParams) {
                            executionDecisions[`${endpointPrefix}: Query: ${param.name}`] = false;
                        }
                        executionDecisions[`${endpointPrefix}: Headers`] = false;
                        executionDecisions[`${endpointPrefix}: Headers List`] = false;
                        if (METHODS_WITH_BODY.includes(endpoint.method)) {
                            executionDecisions[`${endpointPrefix}: Body`] = false;
                        }
                        if (endpoint.method === 'ANY') {
                            executionDecisions[`${endpointPrefix}: Method`] = false;
                        }
                    }

                    // Also disable all auth output ports
                    for (const [key] of Object.entries(authResult.output)) {
                        if (key !== 'Authenticated') {
                            executionDecisions[`Auth: ${key}`] = false;
                        }
                    }

                    return {
                        output,
                        executionDecisions,
                        apiResponse: {
                            requestId: payload.requestId,
                            statusCode: 401,
                            headers: {},
                            body: { error: 'Unauthorized' },
                            streaming: false,
                            isFinal: true,
                        },
                        producedContexts: {
                            apiEndpoint: {
                                requestId: payload.requestId,
                                endpointId: payload.endpointId,
                                streaming: matchedEndpoint.streaming,
                            },
                        },
                    };
                }

                // Auth succeeded — populate auth output ports from function result
                for (const [key, value] of Object.entries(authResult.output)) {
                    if (key !== 'Authenticated') {
                        output[`Auth: ${key}`] = value;
                        executionDecisions[`Auth: ${key}`] = true;
                    }
                }
            }
        }

        // --- Endpoint routing (existing logic) ---

        // Set all endpoint ports to false first, then enable matched ones
        for (const endpoint of endpoints) {
            const endpointPrefix = `${endpoint.method} ${endpoint.path}`;
            executionDecisions[endpointPrefix] = false;
            for (const param of endpoint.pathParams) {
                executionDecisions[`${endpointPrefix}: Path: ${param.name}`] = false;
            }
            for (const param of endpoint.queryParams) {
                executionDecisions[`${endpointPrefix}: Query: ${param.name}`] = false;
            }
            executionDecisions[`${endpointPrefix}: Headers`] = false;
            executionDecisions[`${endpointPrefix}: Headers List`] = false;
            if (METHODS_WITH_BODY.includes(endpoint.method)) {
                executionDecisions[`${endpointPrefix}: Body`] = false;
            }
            if (endpoint.method === 'ANY') {
                executionDecisions[`${endpointPrefix}: Method`] = false;
            }
        }

        // Enable matched endpoint ports
        executionDecisions[prefix] = true;
        output[prefix] = null;

        for (const param of matchedEndpoint.pathParams) {
            const portName = `${prefix}: Path: ${param.name}`;
            executionDecisions[portName] = true;
            output[portName] = parseParamValue(payload.pathParams[param.name], param.type);
        }

        for (const param of matchedEndpoint.queryParams) {
            const portName = `${prefix}: Query: ${param.name}`;
            executionDecisions[portName] = true;
            output[portName] = parseParamValue(payload.queryParams[param.name], param.type);
        }

        // Typed Headers object — only expected headers
        const endpointExpectedHeaders = matchedEndpoint.expectedHeaders || [];
        const combinedHeaders = mergeExpectedHeaders(apiExpectedHeaders, endpointExpectedHeaders);
        const headersObject: Record<string, string> = {};
        for (const headerName of combinedHeaders) {
            const lowerName = headerName.toLowerCase();
            const entry = Object.entries(payload.headers).find(([key]) => key.toLowerCase() === lowerName);
            headersObject[headerName] = entry?.[1] ?? '';
        }
        executionDecisions[`${prefix}: Headers`] = true;
        output[`${prefix}: Headers`] = headersObject;

        // Headers List — full array of all request headers
        executionDecisions[`${prefix}: Headers List`] = true;
        output[`${prefix}: Headers List`] = Object.entries(payload.headers).map(([name, value]) => ({ name, value }));

        if (METHODS_WITH_BODY.includes(matchedEndpoint.method)) {
            executionDecisions[`${prefix}: Body`] = true;
            output[`${prefix}: Body`] = payload.body as DataValue;
        }

        if (matchedEndpoint.method === 'ANY') {
            executionDecisions[`${prefix}: Method`] = true;
            output[`${prefix}: Method`] = payload.method;
        }

        return {
            output,
            executionDecisions,
            producedContexts: {
                apiEndpoint: {
                    requestId: payload.requestId,
                    endpointId: payload.endpointId,
                    streaming: matchedEndpoint.streaming,
                },
            },
        };
    },
});
